import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme, alpha } from '@mui/material/styles';
import { CssBaseline, Box, Button } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { theme } from './theme';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import FullAdminGuard from './components/auth/FullAdminGuard';
import AdminLayout from './components/layout/AdminLayout';
import AuthRedirect from './components/auth/AuthRedirect';
import LoginPage from './pages/LoginPage';
import MyVenues from './pages/admin/MyVenues';
import PreviewMock from './pages/m/PreviewMock';
import PublicWineList from './pages/m/PublicWineList';
import AdminDashboard from './pages/admin/Dashboard';
import UnauthorizedPage from './pages/UnauthorizedPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminResetPasswordPage from './pages/admin/ResetPasswordPage';
import SessionsPage from './pages/admin/SessionsPage';
// Admin User Management Pages
import UserList from './pages/admin/users/UserList';
import UserDetail from './pages/admin/users/UserDetail';
import UserCreate from './pages/admin/users/UserCreate';
import UserEdit from './pages/admin/users/UserEdit';
import UserResetPassword from './pages/admin/users/UserResetPassword';
// Wine Region Management Pages
import RegionList from './pages/admin/regions/RegionList';
import RegionForm from './pages/admin/regions/RegionForm';
import RegionDetail from './pages/admin/regions/RegionDetail';
// Grape Management Pages
import GrapeRoutes from './pages/admin/grapes';
// Producer Management Pages
import ProducerRoutes from './pages/admin/producers';
// Wine Name Management Pages
import WineNameRoutes from './pages/admin/wine-names';
// Wine Management Pages
import WineRoutes from './pages/admin/wines';
// Venue Operations Pages
import VenueRoutes from './pages/admin/venues';
import SupplierRoutes from './pages/admin/suppliers';
import ReviewQueue from './pages/admin/governance/ReviewQueue';
import DuplicateWines from './pages/admin/governance/DuplicateWines';
import Repair from './pages/admin/governance/Repair';
import DataHealth from './pages/admin/governance/DataHealth';


function App() {
  const [mode, setMode] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      const newMode = e.matches ? 'dark' : 'light';
      setMode(newMode);
    };

    handleChange(mediaQuery); // Initial check
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Create a new theme instance with the current mode
  const currentTheme = createTheme({
    ...theme,
    palette: {
      ...theme.palette,
      mode: mode
    }
  });

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />

            {/* Customer-facing wine list — UX validation prototype (no API, no auth). */}
            <Route path="/m/preview-mock" element={<PreviewMock />} />
            {/* Customer-facing wine list — real, fetched from /api/public/wine-list/:slug.
                MUST be registered AFTER /m/preview-mock so the static path wins the match. */}
            <Route path="/m/:slug" element={<PublicWineList />} />
            
            {/* Home route redirects to /admin if authenticated, otherwise shows clean welcome page */}
            <Route path="/" element={
              <AuthRedirect>
                <Box sx={{ 
                  minHeight: '100vh',
                  backgroundColor: currentTheme.palette.background.default,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {/* Login Link in top-right */}
                  <Button
                    component={Link}
                    to="/login"
                    sx={{
                      position: 'absolute',
                      top: 24,
                      right: 24,
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 500,
                      color: currentTheme.palette.text.primary,
                      '&:hover': {
                        backgroundColor: alpha(currentTheme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    Login
                  </Button>

                  {/* Centered Logo */}
                  <Box sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                  }}>
                    <img
                      src="/assets/logo-nota-stock.svg"
                      alt="Nota Stock"
                      style={{
                        height: '120px',
                        width: 'auto',
                        maxWidth: '90vw',
                      }}
                    />
                  </Box>
                </Box>
              </AuthRedirect>
            } />
            
            {/* Protected routes */}
            <Route element={<ProtectedRoute requireAdmin={false} />}>
              <Route path="/change-password" element={<ChangePasswordPage />} />
            </Route>
            
            {/* Protected admin routes with AdminLayout */}
            <Route element={<ProtectedRoute requireAdmin={true} />}>
              <Route element={<AdminLayout />}>
                {/* Admin home — VENUE_USER reaching /admin or /admin/dashboard
                    is bounced to /admin/my-venues by FullAdminGuard. The
                    dashboard shows admin-wide aggregates that don't make
                    sense for a venue-scoped user. */}
                <Route element={<FullAdminGuard />}>
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/dashboard" element={<AdminDashboard />} />
                </Route>

                {/* Sessions (current user's own sessions) is harmless for any role. */}
                <Route path="/admin/sessions" element={<SessionsPage />} />

                {/* Venue picker — for VENUE_USER with multiple assignments. The
                    page itself redirects admins to /admin and single-venue
                    users straight into their venue. */}
                <Route path="/admin/my-venues" element={<MyVenues />} />

                {/* Global admin sections — gated to ADMIN/SYSTEM_ADMIN. */}
                <Route element={<FullAdminGuard />}>
                  {/* Admin User Management Routes */}
                  <Route path="/admin/users" element={<UserList />} />
                  <Route path="/admin/users/create" element={<UserCreate />} />
                  <Route path="/admin/users/:userId" element={<UserDetail />} />
                  <Route path="/admin/users/:userId/edit" element={<UserEdit />} />
                  <Route path="/admin/users/:userId/reset-password" element={<UserResetPassword />} />

                  {/* Wine Region Management Routes */}
                  <Route path="/admin/regions" element={<RegionList />} />
                  <Route path="/admin/regions/create" element={<RegionForm />} />
                  <Route path="/admin/regions/:regionId" element={<RegionDetail />} />
                  <Route path="/admin/regions/:regionId/edit" element={<RegionForm />} />

                  {/* Grape Management Routes */}
                  <Route path="/admin/grapes/*" element={<GrapeRoutes />} />

                  {/* Producer Management Routes */}
                  <Route path="/admin/producers/*" element={<ProducerRoutes />} />

                  {/* Wine Name Management Routes */}
                  <Route path="/admin/wine-names/*" element={<WineNameRoutes />} />

                  {/* Wine Management Routes */}
                  <Route path="/admin/wines/*" element={<WineRoutes />} />

                  {/* Global suppliers + governance — admin only */}
                  <Route path="/admin/suppliers/*" element={<SupplierRoutes />} />
                  <Route path="/admin/governance/review-queue"   element={<ReviewQueue />} />
                  <Route path="/admin/governance/duplicate-wines" element={<DuplicateWines />} />
                  <Route path="/admin/governance/repair"          element={<Repair />} />
                  <Route path="/admin/governance/health"          element={<DataHealth />} />
                </Route>

                {/* ── Venue Operations Routes ───────────────────────────
                    The /admin/venues/* splat handles its own per-venue
                    access guarding inside (VenueAccessGuard) — venue users
                    can reach /admin/venues/<assigned-id>/... but the bare
                    /admin/venues list and /create / /edit are gated to
                    admins only inside VenueRoutes itself. */}
                <Route path="/admin/venues/*" element={<VenueRoutes />} />

                <Route path="/admin/*" element={<AdminDashboard />} />
              </Route>
            </Route>
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
